import {
    canCollect,
    constructionProgress,
    cycleProgress,
    isFull,
    underConstruction,
} from './production.js';
import { production } from './schema.js';
import type { Effects } from './Effects.js';
import type { AssetLoader } from './AssetLoader.js';
import { MIN_ZOOM } from './Camera.js';
import type { Camera } from './Camera.js';
import type { Grid } from './Grid.js';
import type { IsoProjection } from './IsoProjection.js';
import type { Cell, Footprint, GameObject, Placement, PendingBuild, ScreenPoint } from '../types.js';

const UNIT: Footprint = { w: 1, h: 1 };

/**
 * The backdrop/foreground art is cover-fit at zoom 1 with zero slack, so any
 * zoom-out would expose gaps. Pre-scaling by 1 / MIN_ZOOM restores exact
 * coverage at the zoom floor; at zoom 1 the art sits slightly more cropped-in
 * — a deliberate trade for real zoom-out headroom.
 */
const BACKDROP_MARGIN = 1 / MIN_ZOOM;

/**
 * Uniform scale that fits an image inside a box while preserving its aspect
 * ratio — the sprite never spills past the footprint it occupies.
 */
export function fitInside(boxWidth: number, boxHeight: number, imageWidth: number, imageHeight: number): number {
    if (!imageWidth || !imageHeight) return 1;
    return Math.min(boxWidth / imageWidth, boxHeight / imageHeight);
}

/**
 * Scale for a building sprite, matched to the **width** of its footprint.
 *
 * Object art stands on an isometric plinth spanning nearly the whole image
 * width, so fitting to the box's height would bind on the diamond and shrink
 * 2x2/4x4 buildings to a fraction of their tiles. Width alone lands the plinth
 * on the tile at every footprint size and lets the building stand as tall as
 * its art says.
 */
export function fitToFootprintWidth(boxWidth: number, imageWidth: number): number {
    if (!imageWidth) return 1;
    return boxWidth / imageWidth;
}

interface FootprintCorners {
    x0: number;
    x1: number;
    z0: number;
    z1: number;
}

export class Renderer {
    constructor(
        private readonly ctx: CanvasRenderingContext2D,
        private readonly grid: Grid,
        private readonly iso: IsoProjection,
        private readonly assetLoader: AssetLoader,
        private readonly camera: Camera,
        private readonly backgroundColor: string,
        private readonly groundColor: string,
        private readonly groundSprite: string | null | undefined,
        private readonly background: string | null | undefined,
        private readonly foreground: string | null | undefined,
        private readonly foregroundOpacity: number,
        private readonly effects: Effects | null = null,
        private readonly gridColor: string | null = 'rgba(255, 255, 255, 0.18)',
    ) {}

    render(
        placements: Placement[],
        hoveredCell: Cell | null,
        pendingBuild: PendingBuild | null,
        pendingOk: boolean,
    ): void {
        const { ctx, camera } = this;
        if (!camera.width || !camera.height) return;

        ctx.fillStyle = this.backgroundColor;
        ctx.fillRect(0, 0, camera.width, camera.height);

        this.drawBackdrop();
        this.drawGround();
        this.drawGridLines();
        this.drawHighlight(hoveredCell);
        this.drawPlacements(placements);
        this.drawBadges(placements);
        this.drawForeground();
        this.drawGhost(pendingBuild, hoveredCell, pendingOk);
        this.effects?.draw(ctx);
    }

    private footprintCorners(col: number, row: number, footprint: Footprint = UNIT): FootprintCorners {
        const { cellSize } = this.grid;
        return {
            x0: col * cellSize - this.grid.worldWidth / 2,
            x1: (col + footprint.w) * cellSize - this.grid.worldWidth / 2,
            z0: row * cellSize - this.grid.worldHeight / 2,
            z1: (row + footprint.h) * cellSize - this.grid.worldHeight / 2,
        };
    }

    private tracePlan(col: number, row: number, footprint: Footprint, lift = 0): ScreenPoint[] {
        const { x0, x1, z0, z1 } = this.footprintCorners(col, row, footprint);
        const points = [
            this.camera.worldToCanvas(x0, z0),
            this.camera.worldToCanvas(x1, z0),
            this.camera.worldToCanvas(x1, z1),
            this.camera.worldToCanvas(x0, z1),
        ];

        this.ctx.beginPath();
        points.forEach((p, i) => {
            const y = p.y - lift;
            if (i === 0) this.ctx.moveTo(p.x, y);
            else this.ctx.lineTo(p.x, y);
        });
        this.ctx.closePath();
        return points;
    }

    /**
     * Scales with camera.zoom so the whole scene zooms together. Centred on
     * the viewport, not camera.target: with no bleed past cover-fit, tracking
     * the pan would slide the image off-centre and open a gap on the trailing
     * edge. Centred on the viewport, coverage stays exact at every pan
     * position; BACKDROP_MARGIN keeps it exact at every zoom.
     */
    private drawBackdrop(): void {
        const image = this.assetLoader.getImage(this.background);
        if (!image) return;

        const { width, height, zoom } = this.camera;
        const baseScale = Math.max(width / image.width, height / image.height) * BACKDROP_MARGIN;
        const w = image.width * baseScale * zoom;
        const h = image.height * baseScale * zoom;
        this.ctx.drawImage(image, width / 2 - w / 2, height / 2 - h / 2, w, h);
    }

    /**
     * The clouds layer, drawn above the buildings; zoomed and centred the same
     * way as the backdrop.
     */
    private drawForeground(): void {
        const image = this.assetLoader.getImage(this.foreground);
        if (!image || this.foregroundOpacity <= 0) return;

        const { width, height, zoom } = this.camera;
        const baseScale = Math.max(width / image.width, height / image.height) * BACKDROP_MARGIN;
        const w = image.width * baseScale * zoom;
        const h = image.height * baseScale * zoom;

        this.ctx.save();
        this.ctx.globalAlpha = this.foregroundOpacity;
        this.ctx.drawImage(image, width / 2 - w / 2, height / 2 - h / 2, w, h);
        this.ctx.restore();
    }

    private drawGround(): void {
        const ctx = this.ctx;
        const sprite = this.assetLoader.getImage(this.groundSprite);

        if (sprite) {
            // Tile art lives in screen pixels; scale it with the camera or the
            // sprites drift apart from the worldToCanvas-scaled grid.
            const zoom = this.camera.zoom;
            const { width: dw, height: dh } = this.iso.footprintSize(UNIT);
            for (let row = 0; row < this.grid.height; row++) {
                for (let col = 0; col < this.grid.width; col++) {
                    const { x1, z1 } = this.footprintCorners(col, row, UNIT);
                    const bottom = this.camera.worldToCanvas(x1, z1);
                    const centre = this.grid.cellToWorld(col, row);
                    const mid = this.camera.worldToCanvas(centre.x, centre.z);
                    ctx.drawImage(sprite, mid.x - (dw * zoom) / 2, bottom.y - dh * zoom, dw * zoom, dh * zoom);
                }
            }
            return;
        }

        this.tracePlan(0, 0, { w: this.grid.width, h: this.grid.height });
        ctx.fillStyle = this.groundColor;
        ctx.fill();
    }

    private drawGridLines(): void {
        if (!this.gridColor) return;

        const ctx = this.ctx;
        const { cellSize } = this.grid;
        const minX = -this.grid.worldWidth / 2;
        const minZ = -this.grid.worldHeight / 2;
        const maxX = this.grid.worldWidth / 2;
        const maxZ = this.grid.worldHeight / 2;

        ctx.strokeStyle = this.gridColor;
        ctx.lineWidth = this.camera.zoom;
        ctx.beginPath();

        for (let c = 0; c <= this.grid.width; c++) {
            const x = minX + c * cellSize;
            const a = this.camera.worldToCanvas(x, minZ);
            const b = this.camera.worldToCanvas(x, maxZ);
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
        }
        for (let r = 0; r <= this.grid.height; r++) {
            const z = minZ + r * cellSize;
            const a = this.camera.worldToCanvas(minX, z);
            const b = this.camera.worldToCanvas(maxX, z);
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
        }

        ctx.stroke();
    }

    private drawHighlight(hoveredCell: Cell | null): void {
        if (!hoveredCell) return;
        this.tracePlan(hoveredCell.col, hoveredCell.row, UNIT);
        this.ctx.fillStyle = 'rgba(255, 201, 73, 0.45)';
        this.ctx.fill();
    }

    private drawPlacements(placements: Placement[]): void {
        placements.forEach((placement) => {
            const sprite = this.assetLoader.getImage(placement.object.sprite);
            if (sprite) this.drawSprite(placement, sprite);
            else this.drawPlaceholder(placement);
        });
    }

    private drawSprite(
        { object, col, row, footprint }: Placement,
        sprite: HTMLImageElement,
    ): void {
        const { x1, z1 } = this.footprintCorners(col, row, footprint);
        const bottom = this.camera.worldToCanvas(x1, z1);
        const centre = this.grid.footprintToWorld(col, row, footprint);
        const mid = this.camera.worldToCanvas(centre.x, centre.z);

        const settle = this.effects?.popScale(centre.x, centre.z) ?? 1;
        const box = this.iso.footprintSize(footprint);
        const scale = fitToFootprintWidth(box.width, sprite.width)
            * (object.spriteScale ?? 1) * settle * this.camera.zoom;
        const drawWidth = sprite.width * scale;
        const drawHeight = sprite.height * scale;

        this.ctx.drawImage(sprite, mid.x - drawWidth / 2, bottom.y - drawHeight, drawWidth, drawHeight);
    }

    private drawPlaceholder({ object, col, row, footprint }: {
        object: GameObject;
        col: number;
        row: number;
        footprint: Footprint;
    }): void {
        const ctx = this.ctx;
        const colour = object.color ?? '#c97f00';
        const lift = this.iso.heightToPixels(object.placeholderHeight ?? 1) * this.camera.zoom;
        const { x0, x1, z0, z1 } = this.footprintCorners(col, row, footprint);

        const base = {
            top: this.camera.worldToCanvas(x0, z0),
            right: this.camera.worldToCanvas(x1, z0),
            bottom: this.camera.worldToCanvas(x1, z1),
            left: this.camera.worldToCanvas(x0, z1),
        };

        const face = (a: ScreenPoint, b: ScreenPoint) => {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.lineTo(b.x, b.y - lift);
            ctx.lineTo(a.x, a.y - lift);
            ctx.closePath();
            ctx.fill();
        };

        ctx.fillStyle = shade(colour, 0.65);
        face(base.right, base.bottom);

        ctx.fillStyle = shade(colour, 0.45);
        face(base.left, base.bottom);

        ctx.fillStyle = colour;
        this.tracePlan(col, row, footprint, lift);
        ctx.fill();
    }

    private drawGhost(
        pendingBuild: PendingBuild | null,
        hoveredCell: Cell | null,
        ok: boolean,
    ): void {
        if (!pendingBuild || !hoveredCell) return;

        const ctx = this.ctx;
        const { object, moving } = pendingBuild;
        const { col, row } = hoveredCell;
        const footprint = object.footprint ?? UNIT;

        ctx.save();
        ctx.globalAlpha = 0.75;
        const sprite = moving ? this.assetLoader.getImage(object.sprite) : null;
        if (moving && sprite) {
            // A relocation previews the real building at the hovered cell.
            this.drawSprite({ ...moving, col, row }, sprite);
        } else {
            this.drawPlaceholder({ object, col, row, footprint });
        }
        ctx.restore();

        this.tracePlan(col, row, footprint);
        ctx.fillStyle = ok ? 'rgba(110, 220, 110, 0.45)' : 'rgba(220, 80, 80, 0.5)';
        ctx.fill();
        ctx.strokeStyle = ok ? '#48c74a' : '#d64545';
        ctx.lineWidth = 2 * this.camera.zoom;
        ctx.stroke();
    }

    private drawBadges(placements: Placement[]): void {
        const ctx = this.ctx;
        const zoom = this.camera.zoom;

        placements.forEach((placement) => {
            const { object, col, row, footprint, state } = placement;
            const building = underConstruction(state);
            if (!building && !production(object, state.level)) return;

            const centre = this.grid.footprintToWorld(col, row, footprint);
            const mid = this.camera.worldToCanvas(centre.x, centre.z);
            const lift = this.iso.heightToPixels(object.placeholderHeight ?? 1) * zoom;
            const x = mid.x;
            const y = mid.y - lift - 18 * zoom;

            if (building) {
                const raised = constructionProgress(object, state) ?? 0;
                this.drawBadgeCircle(x, y, 'rgba(20, 12, 4, 0.75)');
                this.drawBadgeTrack(x, y);
                ctx.strokeStyle = '#9fd0ff';
                ctx.lineWidth = 3 * zoom;
                ctx.beginPath();
                ctx.arc(x, y, 11 * zoom, -Math.PI / 2, -Math.PI / 2 + raised * Math.PI * 2);
                ctx.stroke();
                ctx.fillStyle = '#9fd0ff';
                ctx.font = `bold ${13 * zoom}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('⚒', x, y + 1);
                return;
            }

            if (canCollect(placement)) {
                this.drawBadgeCircle(x, y, isFull(state) ? '#e0a020' : '#4caf50');
                ctx.fillStyle = '#fff';
                ctx.font = `bold ${13 * zoom}px sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(isFull(state) ? '!' : '✓', x, y + 1);
                return;
            }

            const progress = cycleProgress(object, state);
            if (progress == null) return;

            this.drawBadgeCircle(x, y, 'rgba(20, 12, 4, 0.75)');
            this.drawBadgeTrack(x, y);
            ctx.strokeStyle = '#ffc949';
            ctx.lineWidth = 3 * zoom;
            ctx.beginPath();
            ctx.arc(x, y, 11 * zoom, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
            ctx.stroke();
        });
    }

    private drawBadgeCircle(x: number, y: number, fill: string): void {
        const ctx = this.ctx;
        ctx.beginPath();
        ctx.arc(x, y, 13 * this.camera.zoom, 0, Math.PI * 2);
        ctx.fillStyle = fill;
        ctx.fill();
    }

    private drawBadgeTrack(x: number, y: number): void {
        const ctx = this.ctx;
        const zoom = this.camera.zoom;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
        ctx.lineWidth = 3 * zoom;
        ctx.beginPath();
        ctx.arc(x, y, 11 * zoom, 0, Math.PI * 2);
        ctx.stroke();
    }
}

function shade(hex: string, factor: number): string {
    let value = hex.replace('#', '');
    if (value.length === 3) value = value.split('').map((c) => c + c).join('');
    const int = parseInt(value, 16);
    if (Number.isNaN(int)) return hex;

    const r = Math.round(((int >> 16) & 255) * factor);
    const g = Math.round(((int >> 8) & 255) * factor);
    const b = Math.round((int & 255) * factor);
    return `rgb(${r}, ${g}, ${b})`;
}
